import { useState, type Dispatch, type SetStateAction } from 'react';
import { useGateValue } from '@statsig/react-bindings';
import {
  useUserPreferencesStore,
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
} from '@/shared/stores/user-preferences.store';
import { useHasRoomForAssistant } from './TriggerAssistantDialogLayout';
import { useFillHighlight } from './useFillHighlight';

/** Shared assistant state + derivations returned by {@link useTriggerAssistant}. */
export interface IUseTriggerAssistantReturn {
  /**
   * Whether the integration assistant should be offered: the feature gate is on,
   * the viewport has room for the two-pane layout, and all four selections
   * (integration, version, connection, trigger/poll) are chosen.
   */
  canAssist: boolean;
  /** Current organization id (for the assistant's parent-path routing). */
  organizationId: string;
  /** Current workspace id (for the assistant's parent-path routing). */
  workspaceId: string;
  /**
   * The integration's human-readable display name, reported up from ConfigureStep
   * so the assistant thread is titled "Slack" rather than the raw book id.
   */
  selectedIntegrationDisplayName: string | undefined;
  /** Setter ConfigureStep calls when the resolved integration's name changes. */
  setSelectedIntegrationDisplayName: Dispatch<
    SetStateAction<string | undefined>
  >;
  /** Highlight controller for the configuration form (pulses auto-filled fields). */
  configFill: ReturnType<typeof useFillHighlight>;
}

/**
 * Shared wiring for the trigger-setup assistant, hoisted out of the webhook and
 * poll trigger dialogs which otherwise duplicate it verbatim. Both dialogs feed
 * their four current selections in and get back the gate/room-derived
 * `canAssist`, the org/workspace ids, the integration display-name state, and a
 * `useFillHighlight` instance for the configuration form.
 *
 * The webhook dialog additionally owns a *second* `useFillHighlight` for its
 * filters section (poll has no filters), so that one stays local to the webhook
 * dialog rather than being hoisted here.
 *
 * @param selectedIntegration - The selected integration (book) id, if any
 * @param selectedIntegrationVersion - The selected integration version, if any
 * @param selectedConnection - The selected connection id, if any
 * @param selectedTriggerId - The selected trigger (webhook event) or poll id, if any
 * @returns Shared assistant state and derivations for the host dialog
 */
export function useTriggerAssistant(
  selectedIntegration: string | undefined,
  selectedIntegrationVersion: string | undefined,
  selectedConnection: string | undefined,
  selectedTriggerId: string | undefined,
): IUseTriggerAssistantReturn {
  const isAssistantEnabled = useGateValue('enable_integration_assistant');
  const hasRoomForAssistant = useHasRoomForAssistant();
  const organizationId = useUserPreferencesStore(selectCurrentOrganizationId);
  const workspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);

  // The integration's human-readable name, reported up from ConfigureStep (which
  // resolves the book descriptor). Falls back to the id until it resolves.
  const [selectedIntegrationDisplayName, setSelectedIntegrationDisplayName] =
    useState<string | undefined>();

  // Briefly highlight whatever the assistant just filled in the config form.
  const configFill = useFillHighlight();

  // Shifu needs the book, its version, the connection and the specific
  // trigger/poll to explain/obtain parameters, so only offer the assistant once
  // all four are chosen (always true in edit/readOnly, progressive in create).
  // It also needs room for the two-pane layout, so it's suppressed on narrow
  // viewports.
  const canAssist =
    isAssistantEnabled &&
    hasRoomForAssistant &&
    !!selectedIntegration &&
    !!selectedIntegrationVersion &&
    !!selectedConnection &&
    !!selectedTriggerId;

  return {
    canAssist,
    organizationId,
    workspaceId,
    selectedIntegrationDisplayName,
    setSelectedIntegrationDisplayName,
    configFill,
  };
}
